import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useLatest } from 'ahooks';
import { isNumber } from 'ahooks/es/utils';
import { StartIntervelProps, useCountDownState } from '@/store/useCountDownState';

export type TDate = dayjs.ConfigType;

export interface Options {
    leftTime?: number; // 倒计时剩余时间
    targetDate?: TDate; // 倒计时目标时间
    interval?: number;
    onEnd?: () => void;
    key: string | number; // key表示同一个定时器
}

export interface FormattedRes {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}

const calcLeft = (target?: TDate) => {
    if (!target) {
        return 0;
    }
    // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
    const left = dayjs(target).valueOf() - Date.now();
    return left < 0 ? 0 : left;
};

const parseMs = (milliseconds: number): FormattedRes => {
    return {
        days: Math.floor(milliseconds / 86400000),
        hours: Math.floor(milliseconds / 3600000) % 24,
        minutes: Math.floor(milliseconds / 60000) % 60,
        seconds: Math.floor(milliseconds / 1000) % 60,
        milliseconds: Math.floor(milliseconds) % 1000,
    };
};

const useCountdown = (options: Options) => {
    const { leftTime, targetDate, interval = 1000, onEnd, key } = options || {};

    const target = useMemo<TDate>(() => {
        if ('leftTime' in options) {
            return isNumber(leftTime) && leftTime > 0 ? Date.now() + leftTime : undefined;
        } else {
            return targetDate;
        }
    }, [leftTime, targetDate]);

    const { timeLeft, startIntervel } = useCountDownState(state => ({
        // setCountDown: state.setCountDown,
        timeLeft: state.timeLeft[key] as number | undefined,
        startIntervel: state.startIntervel as (val: StartIntervelProps) => void,
    }));

    const onEndRef = useLatest(onEnd);

    useEffect(() => {
        const _timeLeft = calcLeft(target);
        if (_timeLeft > 0) {
            startIntervel({
                target,
                interval,
                key,
            });
        }
    }, [target, interval, key]);

    useEffect(() => {
        if (timeLeft === 0) onEndRef?.current?.();
    }, [timeLeft]);

    const formattedRes = useMemo(() => parseMs((timeLeft as number) ?? 0), [timeLeft]);

    return [timeLeft, formattedRes] as const;
};

export default useCountdown;
